import React, { FC, useEffect, useState, MouseEvent } from "react";
import { useAppDispatch, useAppSelector } from "redux/hooks";

import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useMediaQuery } from "context/media/MediaContext";

import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { integratorValidationSchema } from "validations/integrator.validation";

import { DIGIPOSTHEME } from "theme/digiPosTheme";

import {
  Button,
  ComboBox,
  IntegratorPassModal,
  Panel,
  PanelHeader,
  Spinner,
  TextField,
  Typography,
} from "components";
import {
  Col,
  ElementWrapper,
  Row,
  StyledFormWrapper,
} from "components/BaseComponents/Flex";
import {
  IIntegrators,
  IIntegratorUpdateRequest,
  IIntegratorUpdateServiceParams,
} from "models/Employee/IntegratoInfo.model";
import { integratorListAsync } from "redux/actions/IntegratorList.action";
import { TokenInclude } from "helpers/GetToken";
import Alert from "components/BaseComponents/Alert/Alert";
import { UpdateIntegrator } from "services/integrators.service";

const IntegratorInfoUpdate: FC<{}> = () => {
  const { t } = useTranslation();
  const { isMobile } = useMediaQuery();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const token = TokenInclude();
  const [integratorsList, setIntegratorsList] = useState([
    { label: "", value: "" },
  ]);
  const [defaultIntegrator, setDefaultIntegrator] = useState({
    label: "",
    value: "",
  });

  const [forgotPass, setForgotPass] = useState<boolean>(false);

  const [isAddSuccess, setIsAddSuccess] = useState<boolean>(false);
  const [isAddError, setIsAddError] = useState<boolean>(false);
  const [txt, setTxt] = useState<string>(
    t("success_update_message").toString()
  );

  const { integratorList, selectedIntegratorObj } = useAppSelector(
    (state) => state.IntegratorList
  );

  const integratorFormat = (list: IIntegrators[]) =>
    list.map((m: IIntegrators) => ({ label: m.Name, value: m.Id }));

  useEffect(() => {
    if (!integratorList?.Integrators) dispatch(integratorListAsync(token));
  }, []);

  useEffect(() => {
    if (integratorList?.Integrators && integratorList.Integrators.length > 0) {
      const newIntList = integratorFormat(integratorList?.Integrators);
      setIntegratorsList(newIntList);
      setDefaultIntegrator(selectedIntegratorObj);
      setValue("selectedIntegrator", selectedIntegratorObj.value);
    }
  }, [integratorList?.Integrators]);

  const defaultFormValues: IIntegratorUpdateRequest = {
    Id: "",
    selectedIntegrator: selectedIntegratorObj.value,
    PassEnc: "",
    Username: "",
  };
  const {
    control,
    handleSubmit,
    errors,
    reset,
    setValue,
    formState: { isSubmitting },
  } = useForm<IIntegratorUpdateRequest>({
    defaultValues: defaultFormValues,
    resolver: yupResolver(integratorValidationSchema(t)),
  });

  const formSubmit = async (data: IIntegratorUpdateRequest): Promise<void> => {
    data.Id = data.selectedIntegrator;

    let params: IIntegratorUpdateServiceParams = { ...data, Token: token };

    const retVal = await UpdateIntegrator(params);
    if (retVal.Confirm) {
      setIsAddSuccess(true);
    } else {
      const errMessage: any = retVal;
      setIsAddError(true);
      setTxt(errMessage);
      return;
    }

    reset();
  };

  const forgotPasswordClick = (event: MouseEvent) => {
    event.stopPropagation();
    setForgotPass((prev) => !prev);
  };

  return (
    <ElementWrapper
      width='872px'
      height={`${isMobile ? "calc(100vh + 320px)" : "100%"}`}
      direction='column'
      justifyContent='flex-start'
      alignItems='flex-start'
      margin='10px auto'
    >
      {(isAddSuccess || isAddError) && (
        <Alert
          alertType={isAddSuccess ? "Success" : "Error"}
          duration={6000}
          isVisible={isAddSuccess || isAddError}
          message={txt}
          setIsAddSuccess={setIsAddSuccess}
          setIsAddError={setIsAddError}
        />
      )}
      <Spinner show={isSubmitting} />
      <Panel width='100%' marginTop='10px'>
        <form onSubmit={handleSubmit(formSubmit)} noValidate>
          <Row backgroundColor={DIGIPOSTHEME.colors.transparent} noMargin>
            <Typography
              margin='10px 0'
              size='21px'
              color={DIGIPOSTHEME.colors.secondaryGraySecondary}
              weight='600'
            >
              {t("Update_Integrator")}
            </Typography>
          </Row>
          <PanelHeader />
          <StyledFormWrapper>
            <Row>
              <Col>
                <ComboBox
                  control={control}
                  label={t("integrator_name").toString()}
                  name='selectedIntegrator'
                  options={integratorsList}
                  defaultValue={{
                    label: defaultIntegrator.label,
                    value: defaultIntegrator.value,
                  }}
                />
              </Col>
            </Row>

            <Row>
              <Col>
                <TextField
                  control={control}
                  error={errors.Username?.message}
                  label={t("user_name").toString()}
                  name='Username'
                  type='alphabetic'
                />
              </Col>
            </Row>

            <Row>
              <Col>
                <TextField
                  control={control}
                  error={errors.PassEnc?.message}
                  label={t("password").toString()}
                  maxLength={8}
                  name='PassEnc'
                  type='masked'
                />
              </Col>
            </Row>

            <Row
              backgroundColor={DIGIPOSTHEME.colors.transparent}
              noMargin
              isCursor
              onClick={(event) => forgotPasswordClick(event)}
            >
              <Col isDisplayFlex={true} justifyContent='center'>
                <Typography
                  width=''
                  size='15px'
                  color={DIGIPOSTHEME.colors.headerRed}
                  margin='10px 0'
                  hoverDecoration='underline'
                  cursor='pointer'
                >
                  {t("password_forgot")}
                </Typography>

                {forgotPass && (
                  <IntegratorPassModal
                    acceptButtonText={t("modal_error_button_title").toString()}
                    message={t("forgot_password_warning").toString()}
                    rejectButtonText={t("no").toString()}
                    setIsOpen={setForgotPass}
                    title={t("information").toString()}
                  />
                )}
              </Col>
            </Row>
          </StyledFormWrapper>

          <Row
            backgroundColor={DIGIPOSTHEME.colors.secondaryOffWhite}
            justifyContent='space-between'
            isResponsive
            noMargin
          >
            <Col>
              <Button
                type='button'
                width={isMobile ? "100%" : "100px"}
                text={`← ${t("back")}`}
                onClick={() => navigate(-1)}
                color='light'
              />
            </Col>
            <Col maxWidth='130px'>
              <Button text={t("update_button").toString()} type='submit' />
            </Col>
          </Row>
        </form>
      </Panel>
    </ElementWrapper>
  );
};

export default IntegratorInfoUpdate;
